import { useMemo } from 'react'

import type { RecentHotelSearches } from '@/components/TripActivity/types'
import { sortByCreationDateTimeDesc } from '@/components/TripActivity/utils'
import useBootstrapData from '@/hooks/useBootstrapData'
import useClientSideSignedInData from '@/hooks/useClientSideSignedInData'
import useRecentSearchCard from '@/hooks/useRecentSearchCard'
import useSeti from '@/hooks/useSeti'

import {
  type DestinationWithCityId,
  getDestinationCityId,
  getPrefillFromLastSearch,
  type LastSearchPrefill,
} from './prefillFromLastSearch'

export type LastSearchedDestination = DestinationWithCityId

/**
 * Dates and travellers from the user's last STAY search to the city the destination field would
 * pre-fill, ready to apply to the hotel form.
 *
 * The destination isn't simply the first row of the recent-searches API: the autocomplete merges
 * abandoned and viewed hotels in, drops neighbourhoods, and falls back to local storage. So the
 * search is looked up by the city the autocomplete reports (`onLastSearchedItemChange`), and when
 * none of the user's recent searches is for that city there is nothing to pre-fill — the form must
 * never show one city with another trip's dates and travellers.
 *
 * Reads the same react-query entry as the autocomplete's recents (same cguid, session and
 * SETI-driven request options), so it adds no network request.
 */
export function useLastSearchPrefill(destination: LastSearchedDestination | null): {
  prefill: LastSearchPrefill | null
  lastSearch: RecentHotelSearches | null
} {
  const {
    appName,
    appVersion,
    webstats: { clientGUID },
    signInStatus: { signedIn: serverSignedIn },
  } = useBootstrapData()
  const { signedIn: clientSideSignedIn } = useClientSideSignedInData()
  const signedIn = serverSignedIn || clientSideSignedIn

  const sortByCreationDateTime =
    useSeti({ experimentCode: 'DESK_MWEB_HTL_RECENT_SEARCH_LOGIC_UPDATE', shouldFireImpression: false }) ===
    'VARIANT'

  const { data } = useRecentSearchCard(clientGUID, { appName, appVersion }, ['STAY'], signedIn)

  const destinationCityId = getDestinationCityId(destination)

  return useMemo(() => {
    if (!destinationCityId) return { lastSearch: null, prefill: null }

    const stays = data.filter((item): item is RecentHotelSearches => item.productType === 'STAY')
    const ordered = sortByCreationDateTime ? sortByCreationDateTimeDesc(stays) : stays
    // Several searches can be for the same city; the first in the same order the list is shown in
    // is the one the user most recently (or most relevantly) made.
    const lastSearch = ordered.find((stay) => String(stay.cityId) === destinationCityId) ?? null
    return { lastSearch, prefill: getPrefillFromLastSearch(lastSearch) }
  }, [data, sortByCreationDateTime, destinationCityId])
}
